import React, { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
  Box
} from '@chakra-ui/react'

import axios from 'lib/axios'
import { useSearchRentalsForEvictionQuery } from 'graphql/generated'

interface CreateEvictionModalProps {
  isOpen: boolean
  isDisabled: boolean
  onClose: () => void
}

const CreateEvictionModal: React.FC<CreateEvictionModalProps> = ({
  isOpen,
  isDisabled,
  onClose
}) => {
  const toast = useToast()

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedRentalId, setSelectedRentalId] = useState<string | null>(null)
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const [{ data: searchData, fetching: searchFetching }] = useSearchRentalsForEvictionQuery({
    variables: { search: `%${debouncedSearch}%` },
    pause: debouncedSearch.length < 2,
    requestPolicy: 'network-only',
    context: React.useMemo(() => ({ suspense: false }), [])
  })

  const handleClose = () => {
    setSearchInput('')
    setDebouncedSearch('')
    setSelectedRentalId(null)
    setSelectedAddress(null)
    setNote('')
    onClose()
  }

  const handleSubmit = async () => {
    if (!selectedRentalId || isDisabled) return

    setIsSubmitting(true)
    try {
      await axios.post('/admin/evictions', {
        rental_id: selectedRentalId,
        note: note || undefined
      })
      toast({ title: 'Eviction created', status: 'success', duration: 3000 })
      handleClose()
    } catch (err) {
      toast({ title: 'Failed to create eviction', status: 'error', duration: 5000 })
    } finally {
      setIsSubmitting(false)
    }
  }

  const rentals = searchData?.rentals ?? []

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Eviction</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Text fontWeight='semibold'>Search Active Rental by Address</Text>
              <Input
                placeholder='10 Grand St...'
                value={searchInput}
                isDisabled={isDisabled}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  setSelectedRentalId(null)
                  setSelectedAddress(null)
                }}
              />
            </Stack>

            {searchFetching && <Spinner size='sm' />}

            {!searchFetching && debouncedSearch.length >= 2 && rentals.length === 0 && (
              <Text color='gray.500' fontSize='sm'>
                No active rentals found
              </Text>
            )}

            {rentals.length > 0 && !selectedRentalId && (
              <Stack spacing={1}>
                {rentals.map((rental) => {
                  const address = rental.portfolio_home?.home?.address?.display_line_1 ?? ''
                  const tenant = rental.user
                    ? `${rental.user.first_name} ${rental.user.last_name}`
                    : ''
                  return (
                    <Box
                      key={rental.id}
                      p={2}
                      border='1px solid'
                      borderColor='gray.200'
                      borderRadius='md'
                      cursor={isDisabled ? 'not-allowed' : 'pointer'}
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => {
                        if (isDisabled) return
                        setSelectedRentalId(rental.id)
                        setSelectedAddress(address)
                        setSearchInput(address)
                      }}
                    >
                      <Text fontWeight='semibold'>{address}</Text>
                      {tenant && (
                        <Text fontSize='sm' color='gray.600'>
                          {tenant}
                        </Text>
                      )}
                    </Box>
                  )
                })}
              </Stack>
            )}

            {selectedRentalId && (
              <Box p={2} bg='green.50' borderRadius='md' border='1px solid' borderColor='green.200'>
                <Text fontWeight='semibold' color='green.700'>
                  Selected: {selectedAddress}
                </Text>
              </Box>
            )}

            <Stack spacing={1}>
              <Text fontWeight='semibold'>Note (optional)</Text>
              <Textarea
                placeholder='Add a note...'
                value={note}
                isDisabled={isDisabled}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' size='xs' mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme='teal'
            size='xs'
            onClick={handleSubmit}
            isDisabled={isDisabled || !selectedRentalId || isSubmitting}
            isLoading={isSubmitting}
          >
            Create Eviction
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CreateEvictionModal
